import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import { useFloatingLabelVirtualizedOptions } from '@/storybook/components/FormCollection/hooks/useFloatingLabelVirtualizedOptions';
import {
  VirtualRowMeta,
  type VirtualizedListState,
} from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import styles from '@/storybook/components/FormCollection/SingleSelect/SingleSelectFloatingLabel.module.css';
import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import {
  EMPTY_CATEGORY_KEYS,
  getCategoriesToDisplay,
  getCategoryKey,
  sortOptionsByCategory,
} from '@/storybook/components/FormCollection/utils/categoryOptions';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getSelectItemLabelContent,
  isOptionSelected,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui/selectItem';
import { useCallback, useMemo, type ReactNode, type RefObject } from 'react';

type UseSingleSelectVirtualizationFloatingLabelParams<T> = {
  options: SelectItem<T>[];
  selectedValues: T[];
  disabled: boolean;
  noCategoryLabel: string;
  onOptionClick: (selectedValue: T) => void;
  collapsibleCategories: boolean;
  isCategoryCollapsed?: (categoryKey: string) => boolean;
  onToggleCategory: (categoryKey: string) => void;
};

type UseSingleSelectVirtualizationFloatingLabelReturn<T> = {
  virtualizedList: VirtualizedListState<T>;
  parentRef: RefObject<HTMLElement | null>;
  renderVirtualRow: (vMeta: VirtualRowMeta<T>) => ReactNode;
};

type SingleSelectVirtualizedOptionRowProps<T> = {
  option: SelectItem<T>;
  isSelected: boolean;
  disabled: boolean;
  onOptionClick: (selectedValue: T) => void;
  isInset: boolean;
};

const NO_SELECTED_OPTIONS: never[] = [];

const SingleSelectVirtualizedOptionRow = <T,>({
  option,
  isSelected,
  disabled,
  onOptionClick,
  isInset,
}: SingleSelectVirtualizedOptionRowProps<T>) => {
  const handleClick = useCallback(() => onOptionClick(option.value), [onOptionClick, option.value]);

  return (
    <li className={styles.dropdownOptionListItem}>
      <TreeItem
        className={isInset ? styles.insetOptionRow : undefined}
        label={extractSelectItemLabelText(option)}
        labelContent={getSelectItemLabelContent(option)}
        variant="singleSelect"
        level={0}
        isSelected={isSelected}
        isDisabled={disabled}
        onClick={handleClick}
        endContent={option.additionalInfo}
        data-testid={`singleSelectFloatingLabelOption-${createSelectItemKey(option)}`}
      />
    </li>
  );
};

export const useSingleSelectVirtualizationFloatingLabel = <T,>({
  options,
  selectedValues,
  disabled,
  noCategoryLabel,
  onOptionClick,
  collapsibleCategories,
  isCategoryCollapsed,
  onToggleCategory,
}: UseSingleSelectVirtualizationFloatingLabelParams<T>): UseSingleSelectVirtualizationFloatingLabelReturn<T> => {
  const sortedOptions = useMemo(
    () => (options.some(option => option.category != null) ? sortOptionsByCategory(options, noCategoryLabel) : options),
    [options, noCategoryLabel],
  );

  const categoriesToDisplay = useMemo(() => getCategoriesToDisplay(sortedOptions), [sortedOptions]);

  const { virtualizedList, parentRef } = useFloatingLabelVirtualizedOptions<T>({
    selectedOptions: NO_SELECTED_OPTIONS,
    notSelectedOptions: sortedOptions,
    categoriesToDisplay,
    noCategoryLabel,
    isCategoryCollapsed,
  });

  const selectedCategoryKeys = useMemo(() => {
    if (!collapsibleCategories) return EMPTY_CATEGORY_KEYS;
    return new Set(sortedOptions.filter(option => isOptionSelected(option, selectedValues)).map(getCategoryKey));
  }, [collapsibleCategories, sortedOptions, selectedValues]);

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>) => {
      if (vMeta.kind === 'category' && vMeta.category) {
        const { categoryKey, isCollapsed = false } = vMeta;
        const handleToggle = categoryKey ? () => onToggleCategory(categoryKey) : undefined;
        return (
          <ListHeader
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
            collapsible={collapsibleCategories}
            isCollapsed={isCollapsed}
            isSelected={isCollapsed && !!categoryKey && selectedCategoryKeys.has(categoryKey)}
            alignCaretWithLabels
            onToggle={handleToggle}
          />
        );
      }
      if (!vMeta.option) return null;
      return (
        <SingleSelectVirtualizedOptionRow<T>
          option={vMeta.option}
          isSelected={!!isOptionSelected(vMeta.option, selectedValues)}
          disabled={disabled}
          onOptionClick={onOptionClick}
          isInset={collapsibleCategories}
        />
      );
    },
    [collapsibleCategories, selectedCategoryKeys, onToggleCategory, selectedValues, disabled, onOptionClick],
  );

  return { virtualizedList, parentRef, renderVirtualRow };
};
